import * as React from 'react';
import {
    Box,
    Button,
    Chip,
    Checkbox,
    Drawer,
    FormControlLabel,
    MenuItem,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import HistoryIcon from '@mui/icons-material/History';
import { useRecordContext, useStore, useTranslate } from 'react-admin';
import { useCanAccess } from '@react-admin/ra-rbac';
import { useLocation, useSearchParams } from 'react-router-dom';
import BulkMergePage from '../user/BulkMergePage';
import MergeHistoryPage from '../user/MergeHistoryPage';
import MergeReportPage from '../user/MergeReportPage';
import { MERGE_RESOURCES, MERGE_VIEWS } from '../user/mergeNavigation';

const SchoolMergeTab = () => {
    const school = useRecordContext();
    const translate = useTranslate();
    const { canAccess, isPending } = useCanAccess({
        action: 'read',
        resource: 'merge',
    });
    const { pathname } = useLocation();
    const [params, setParams] = useSearchParams();
    const [busy, setBusy] = React.useState(false);
    const [helpOpen, setHelpOpen] = React.useState(false);
    const [autoOpenHelp, setAutoOpenHelp] = useStore<boolean>(
        'mergeHelpAutoOpen',
        true
    );
    const schoolType = Number(school?.public?.type);
    const isUpperSecondarySchool =
        (schoolType >= 131 && schoolType <= 139) ||
        schoolType === 144 ||
        schoolType === 147;
    const resource =
        MERGE_RESOURCES.find(value => value === params.get('resource')) ||
        (isUpperSecondarySchool ? 'student' : 'pupil');
    const view =
        MERGE_VIEWS.find(value => value === params.get('view')) || 'export';
    const isSectionActive =
        !isPending &&
        canAccess &&
        pathname.endsWith('/yhdistaminen') &&
        Boolean(school?.id);
    const wasSectionActive = React.useRef(false);

    React.useEffect(() => {
        if (isSectionActive && !wasSectionActive.current && autoOpenHelp)
            setHelpOpen(true);
        wasSectionActive.current = isSectionActive;
    }, [autoOpenHelp, isSectionActive]);

    // FormTab also renders inactive panels; do not fetch merge data there.
    if (!isSectionActive) return null;

    const change = (key: string, value: string) => {
        const next = new URLSearchParams(params);
        next.set(key, value);
        setParams(next);
    };
    const context = {
        school: String(school.id),
        resource,
        onBusyChange: setBusy,
    };
    const helpSections = [
        {
            id: 'overview',
            title: 'merge.workspace.title',
            paragraphs: [
                'merge.bulk.report_workflow',
                'merge.bulk.report_workflow_note',
            ],
        },
        {
            id: 'export',
            title: 'merge.workspace.export',
            paragraphs: [
                'merge.bulk.report_note',
                'merge.bulk.account_details_help',
                'merge.bulk.sort_export_help',
                'merge.bulk.search_help',
                'merge.bulk.filters_help',
                'merge.bulk.identifier_domain_help',
            ],
        },
        {
            id: 'merge',
            title: 'merge.workspace.merge',
            paragraphs: [
                'merge.bulk.sort_merge_help',
                'merge.bulk.filters_help',
                'merge.bulk.sort_merge_reminder',
            ],
        },
        {
            id: 'report',
            title: 'merge.workspace.report',
            paragraphs: [
                'merge.history.report_help',
                'merge.history.report_note',
                'merge.history.mpass_help',
                'merge.history.report_missing_audit',
            ],
        },
        {
            id: 'history',
            title: 'merge.workspace.history',
            paragraphs: ['merge.history.description', 'merge.history.mpass_help'],
        },
    ];

    return (
        <Box
            width="100%"
            onKeyDown={event => {
                if (
                    event.key === 'Enter' &&
                    event.target instanceof HTMLInputElement
                )
                    event.preventDefault();
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
            >
                <TextField
                    select
                    size="small"
                    label={translate('merge.workspace.role')}
                    value={resource}
                    disabled={busy}
                    onChange={event => change('resource', event.target.value)}
                    sx={{ minWidth: 240, my: 2 }}
                >
                    {MERGE_RESOURCES.map(value => (
                        <MenuItem key={value} value={value}>
                            {translate(`resources.${value}.name`, {
                                smart_count: 2,
                            })}
                        </MenuItem>
                    ))}
                </TextField>
                <Button
                    type="button"
                    startIcon={<HelpOutlineIcon />}
                    onClick={() => setHelpOpen(true)}
                    aria-haspopup="dialog"
                    aria-expanded={helpOpen}
                >
                    {translate('merge.workspace.help')}
                </Button>
            </Stack>
            <Drawer
                anchor="right"
                open={helpOpen}
                onClose={() => setHelpOpen(false)}
                PaperProps={{
                    role: 'dialog',
                    'aria-modal': true,
                    'aria-labelledby': 'merge-help-title',
                    sx: { width: 480, maxWidth: '100vw' },
                }}
            >
                <Box p={3}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={2}
                        mb={3}
                    >
                        <Typography id="merge-help-title" variant="h6">
                            {translate('merge.workspace.help')}
                        </Typography>
                        <Button type="button" onClick={() => setHelpOpen(false)}>
                            {translate('ra.action.close')}
                        </Button>
                    </Stack>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={autoOpenHelp}
                                onChange={event =>
                                    setAutoOpenHelp(event.target.checked)
                                }
                            />
                        }
                        label={translate(
                            'merge.workspace.help_auto_open'
                        )}
                        sx={{ mb: 2 }}
                    />
                    {helpSections.map(section => (
                        <Box
                            component="details"
                            key={`${section.id}:${view}:${helpOpen}`}
                            open={section.id === 'overview'}
                            sx={{
                                mb: 2,
                                pb: 2,
                                borderBottom: 1,
                                borderColor: 'divider',
                            }}
                        >
                            <Box
                                component="summary"
                                sx={{ cursor: 'pointer' }}
                            >
                                <Typography
                                    component="span"
                                    variant="subtitle1"
                                    fontWeight="bold"
                                >
                                    {translate(section.title)}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ mt: 0.5, color: 'text.secondary' }}
                                >
                                    {translate(
                                        `merge.workspace.help_${section.id}`
                                    )}
                                </Typography>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                {section.paragraphs.map(key => (
                                    <Typography
                                        key={key}
                                        variant="body2"
                                        sx={{ mb: 1.5, whiteSpace: 'pre-line' }}
                                    >
                                        {translate(key)}
                                    </Typography>
                                ))}
                                {section.id === 'overview' && (
                                    <Box
                                        role="group"
                                        aria-label={translate(
                                            'merge.workspace.license_legend_title'
                                        )}
                                        sx={{ mt: 2 }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ mb: 1 }}
                                        >
                                            {translate(
                                                'merge.workspace.license_legend_title'
                                            )}
                                        </Typography>
                                        <Stack spacing={1}>
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing={1}
                                            >
                                                <CheckCircleIcon
                                                    fontSize="small"
                                                    color="success"
                                                />
                                                <Typography variant="body2">
                                                    {translate(
                                                        'merge.workspace.license_legend_valid'
                                                    )}
                                                </Typography>
                                            </Stack>
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing={1}
                                            >
                                                <EventBusyIcon
                                                    fontSize="small"
                                                    color="disabled"
                                                />
                                                <Typography variant="body2">
                                                    {translate(
                                                        'merge.workspace.license_legend_not_valid'
                                                    )}
                                                </Typography>
                                            </Stack>
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing={1}
                                            >
                                                <HistoryIcon fontSize="small" />
                                                <Typography variant="body2">
                                                    {translate(
                                                        'merge.workspace.license_legend_extended'
                                                    )}
                                                </Typography>
                                            </Stack>
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing={1}
                                            >
                                                <Chip
                                                    size="small"
                                                    color="success"
                                                    variant="filled"
                                                    label={translate(
                                                        'merge.workspace.license_legend_overlapping_sample'
                                                    )}
                                                />
                                                <Typography variant="body2">
                                                    {translate(
                                                        'merge.workspace.license_legend_overlapping'
                                                    )}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Drawer>
            <Tabs
                value={view}
                onChange={(_, value) => change('view', value)}
                variant="scrollable"
                scrollButtons="auto"
                aria-label={translate('merge.workspace.title')}
                sx={{ mb: 2 }}
            >
                {MERGE_VIEWS.map(value => (
                    <Tab
                        key={value}
                        value={value}
                        label={translate(`merge.workspace.${value}`)}
                        disabled={busy}
                        id={`merge-tab-${value}`}
                        aria-controls={`merge-panel-${value}`}
                    />
                ))}
            </Tabs>
            <Box
                role="tabpanel"
                id={`merge-panel-${view}`}
                aria-labelledby={`merge-tab-${view}`}
            >
                {(view === 'export' || view === 'merge') && (
                    <BulkMergePage
                        key={`${school.id}:${resource}`}
                        {...context}
                        mode={view}
                    />
                )}
                {view === 'report' && (
                    <MergeReportPage
                        key={`${school.id}:${resource}`}
                        {...context}
                    />
                )}
                {view === 'history' && (
                    <MergeHistoryPage
                        key={`${school.id}:${resource}`}
                        {...context}
                    />
                )}
            </Box>
        </Box>
    );
};

export default SchoolMergeTab;
